// AI服务模块 - 完整的故事创作流程
import { RealAIService } from './realAiService.js'
import { SingleCallAIService } from './singleCallAiService.js'
import { DoubaoImageServiceV2 } from './doubaoImageServiceV2.js'
import settingsService from './settingsService.js'
import { ErrorHandler } from '../utils/errorHandler.js'

export class AIService {
  // 完整的故事创作流程 - 使用单次API调用策略
  static async createCompleteStory(content, genre, style, onProgress) {
    try {
      console.log('🎬 开始AI故事创作流程（单次API调用策略）...')
      
      // 逐步显示进度，模拟真实的生成过程
      onProgress && onProgress(5, "正在分析创意内容...")
      await this.delay(200) // 模拟分析时间
      
      onProgress && onProgress(12, "正在生成故事大纲...")
      await this.delay(300) // 模拟大纲生成时间
      
      onProgress && onProgress(20, "正在创建角色档案...")
      await this.delay(250) // 模拟角色创建时间
      
      onProgress && onProgress(28, "正在设计章节结构...")
      await this.delay(200) // 模拟结构设计时间
      
      onProgress && onProgress(35, "正在准备AI调用...")
      await this.delay(150) // 模拟准备时间
      
      onProgress && onProgress(45, "正在生成故事内容...")
      
      // 实际调用AI生成故事，并在过程中更新进度
      const completeStory = await this.generateStoryWithProgress(content, genre, style, onProgress)
      console.log('✅ 单次API调用生成完成:', completeStory.title)
      
      onProgress && onProgress(100, "精彩互动故事创作完成！")
      
      return completeStory
    } catch (error) {
      console.error('❌ 故事创作失败:', error)
      
      // 使用错误处理系统显示错误弹窗
      ErrorHandler.handleStoryGenerationError(error, content, genre, style)
      
      // 直接抛出错误，不再生成备用故事
      throw error
    }
  }

  // 辅助方法：延迟执行
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // 带进度条的故事生成方法
  static async generateStoryWithProgress(content, genre, style, onProgress) {
    try {
      // 开始API调用
      onProgress && onProgress(50, "正在调用AI大模型...")
      
      // 启动进度动画
      const progressInterval = this.startProgressAnimation(50, 90, onProgress, "正在生成故事内容...")
      
      try {
        // 实际调用AI服务
        const completeStory = await SingleCallAIService.generateCompleteStory(content, genre, style)
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(90, "正在生成镜头图片...")
        await this.delay(800)
        
        onProgress && onProgress(95, "正在完成最终整合...")
        await this.delay(300)
        
        return completeStory
      } catch (error) {
        // 停止进度动画
        clearInterval(progressInterval)
        throw error
      }
    } catch (error) {
      console.error('❌ 故事生成过程中出错:', error)
      throw error
    }
  }

  // 启动进度动画
  static startProgressAnimation(startProgress, endProgress, onProgress, message) {
    let currentProgress = startProgress
    const totalSteps = endProgress - startProgress
    const duration = 4000 // 4秒内完成动画
    const interval = 150 // 每150ms更新一次
    const baseIncrement = totalSteps / (duration / interval)
    
    // 随机的进度消息
    const progressMessages = [
      "正在生成故事内容...",
      "正在处理AI响应...",
      "正在构建故事结构...",
      "正在优化内容质量...",
      "正在生成对话内容...",
      "正在设计角色互动...",
      "正在完善故事细节..."
    ]
    
    let messageIndex = 0
    
    return setInterval(() => {
      if (currentProgress < endProgress) {
        // 添加随机变化，让进度看起来更真实
        const randomFactor = 0.8 + Math.random() * 0.4 // 0.8到1.2之间的随机因子
        const increment = baseIncrement * randomFactor
        currentProgress = Math.min(currentProgress + increment, endProgress)
        
        // 随机更新消息
        if (Math.random() < 0.3) { // 30%的概率更新消息
          messageIndex = (messageIndex + 1) % progressMessages.length
          onProgress && onProgress(Math.floor(currentProgress), progressMessages[messageIndex])
        } else {
          onProgress && onProgress(Math.floor(currentProgress), message)
        }
      }
    }, interval)
  }

  // 为分章节故事生成镜头图片
  static async generateSceneImagesForChapters(chapters, characters, style) {
    console.log('🖼️ 为分章节故事生成镜头图片...')
    
    const imageConfig = settingsService.getImageGenerationConfig()
    const doubaoConfig = settingsService.getDoubaoImageConfig()
    const allScenes = []
    
    for (const chapter of chapters) {
      for (const scene of chapter.scenes) {
        // 如果当前选择的图像模型是豆包，优先使用豆包
        if (imageConfig.model === 'doubao-seedream-4-0' && imageConfig.apiKey && imageConfig.apiUrl) {
          try {
            // 优先使用豆包-seedream-4.0
            const imageUrl = await DoubaoImageServiceV2.generateImage(
              DoubaoImageServiceV2.buildSceneImagePrompt(scene, style, characters),
              { size: '2K' }
            )
            scene.sceneImage = imageUrl
            console.log(`✅ 豆包-seedream-4.0镜头图片生成成功: ${scene.title}`)
          } catch (error) {
            console.warn(`⚠️ 豆包镜头图片生成失败: ${scene.title}`, error.message)
            // 回退到其他图像生成API
            if (imageConfig.apiKey && imageConfig.apiUrl) {
              try {
                // 使用其他图像生成API
                const imageUrl = await RealAIService.generateSceneImage(scene.imagePrompt, style)
                console.log(`✅ 场景图片生成成功: ${scene.title}`)
                scene.sceneImage = imageUrl
              } catch (fallbackError) {
                console.warn(`⚠️ 备用场景图片生成失败: ${scene.title}`, fallbackError.message)
                scene.sceneImage = ''
              }
            } else {
              scene.sceneImage = ''
            }
          }
        } else if (imageConfig.apiKey && imageConfig.apiUrl) {
          try {
            // 使用其他图像生成API
            const imageUrl = await RealAIService.generateSceneImage(scene.imagePrompt, style)
            console.log(`✅ 场景图片生成成功: ${scene.title}`)
            scene.sceneImage = imageUrl
          } catch (error) {
            console.warn(`⚠️ 场景图片生成失败: ${scene.title}`, error.message)
            scene.sceneImage = ''
          }
        } else {
          // 使用模拟图片
          scene.sceneImage = scene.imageUrl || `https://picsum.photos/800/600?random=${Math.floor(Math.random() * 1000)}`
        }
        allScenes.push(scene)
      }
    }
    
    return allScenes
  }

  // 为分章节故事构建连接关系
  static async buildStoryConnectionsForChapters(chapters) {
    console.log('🔗 为分章节故事构建连接关系...')
    
    const connections = []
    
    for (const chapter of chapters) {
      for (let i = 0; i < chapter.scenes.length; i++) {
        const currentScene = chapter.scenes[i]
        
        // 添加镜头间的线性连接
        if (i < chapter.scenes.length - 1) {
          const nextScene = chapter.scenes[i + 1]
          connections.push({
            id: `conn-${currentScene.id}-${nextScene.id}`,
            source: currentScene.id,
            target: nextScene.id,
            type: 'linear',
            label: '继续'
          })
        }
        
        // 添加分支选择的连接
        if (currentScene.choices && currentScene.choices.length > 0) {
          for (const choice of currentScene.choices) {
            if (choice.targetNodeId) {
              connections.push({
                id: `conn-${currentScene.id}-${choice.targetNodeId}`,
                source: currentScene.id,
                target: choice.targetNodeId,
                type: 'choice',
                label: choice.text,
                choiceId: choice.id
              })
            }
          }
        }
      }
    }
    
    console.log('✅ 连接关系构建完成，连接数:', connections.length)
    return connections
  }

  // 完成分章节故事的最终整合
  static async finalizeChapterBasedStory(storyData, scenesWithImages, storyConnections) {
    console.log('📚 完成分章节故事最终整合...')
    
    const completeStory = {
      id: `story-${Date.now()}`,
      title: storyData.outline.title,
      summary: storyData.outline.summary,
      mainPlot: storyData.outline.mainPlot,
      genre: storyData.outline.genre || '冒险',
      style: storyData.outline.style || '现代',
      status: 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // 章节数据
      chapters: storyData.chapters,
      
      // 角色数据
      characters: storyData.characters,
      
      // 场景数据（扁平化）
      scenes: scenesWithImages,
      
      // 连接关系
      connections: storyConnections,
      
      // 分支点和结局
      branchPoints: storyData.outline.branchPoints || [],
      endings: storyData.outline.endings || [],
      
      // 统计信息
      stats: {
        totalChapters: storyData.chapters.length,
        totalScenes: scenesWithImages.length,
        totalCharacters: storyData.characters.length,
        totalConnections: storyConnections.length,
        completedScenes: scenesWithImages.filter(s => s.content && s.content.length > 100).length
      }
    }
    
    console.log('✅ 分章节故事整合完成:', {
      title: completeStory.title,
      chapters: completeStory.stats.totalChapters,
      scenes: completeStory.stats.totalScenes,
      characters: completeStory.stats.totalCharacters
    })
    
    return completeStory
  }

  // 生成完整的故事结构 - 基于API配置决定使用真实数据还是模拟数据
  static async generateStoryStructure(outline, onProgress) {
    // 检查是否配置了语言模型API
    const languageConfig = settingsService.getLanguageModelConfig()
    
    if (languageConfig.apiKey && languageConfig.apiUrl) {
      console.log('✅ 语言模型API已配置，使用真实API生成故事结构...')
      try {
        // 启动进度动画
        const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成章节结构...")
        
        const result = await RealAIService.generateStoryStructure(outline)
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(100, "章节结构生成完成！")
        console.log('✅ 真实API生成故事结构成功')
        return result
      } catch (error) {
        console.error('❌ 真实API调用失败:', error)
        // API配置成功但调用失败，尝试基于大纲生成基础结构
        console.log('🔄 尝试基于大纲生成基础故事结构...')
        try {
          const fallbackStructure = this.generateFallbackStoryStructure(outline)
          console.log('✅ 基础故事结构生成成功')
          return fallbackStructure
        } catch (fallbackError) {
          console.error('❌ 基础故事结构生成也失败:', fallbackError)
          // 如果备用方案也失败，提供更详细的错误信息
          if (error.message.includes('Failed to fetch')) {
            throw new Error(`网络连接失败，请检查网络连接后重试`)
          } else if (error.message.includes('ERR_CONNECTION_CLOSED')) {
            throw new Error(`API连接被关闭，请检查API配置或稍后重试`)
          } else {
            throw new Error(`故事结构生成失败: ${error.message}`)
          }
        }
      }
    }
    
    console.log('⚠️ 语言模型API未配置，使用模拟数据生成故事结构...')
    
    // 启动进度动画
    const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成章节结构...")
    
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // 停止进度动画
    clearInterval(progressInterval)
    
    onProgress && onProgress(100, "章节结构生成完成！")
    
    // 基于真实大纲创建模拟结构
    const structure = {
      id: Date.now().toString(),
      title: outline.title,
      chapters: outline.chapters || [
        {
          id: 1,
          title: "故事开端",
          scenes: 3,
          description: "故事的开端，建立世界观和角色关系"
        },
        {
          id: 2,
          title: "冲突与选择",
          scenes: 3,
          description: "主要冲突的展开，分支选择点"
        },
        {
          id: 3,
          title: "结局",
          scenes: 2,
          description: "故事的高潮部分，通向不同的结局"
        }
      ],
      branches: outline.branches || [
        {
          id: "branch-1",
          fromChapter: 1,
          fromScene: 3,
          choices: [
            { text: "勇敢面对", targetChapter: 2, targetScene: 1, path: "brave" },
            { text: "谨慎行事", targetChapter: 2, targetScene: 2, path: "cautious" }
          ]
        },
        {
          id: "branch-2",
          fromChapter: 2,
          fromScene: 2,
          choices: [
            { text: "坚持原则", targetChapter: 3, targetScene: 1, path: "justice" },
            { text: "寻求和解", targetChapter: 3, targetScene: 2, path: "peace" }
          ]
        }
      ],
      connections: []
    }
    
    // 为章节生成具体的场景数组（只有在章节没有场景时才生成）
    for (const chapter of structure.chapters) {
      if (!chapter.scenes || chapter.scenes.length === 0) {
        chapter.scenes = this.generateChapterScenes(chapter, structure, outline)
      }
    }
    
    return structure
  }

  // 生成备用故事结构 - 当API调用失败时使用
  static generateFallbackStoryStructure(outline) {
    console.log('🔄 生成备用故事结构，基于大纲:', outline.title)
    
    const structure = {
      id: Date.now().toString(),
      title: outline.title,
      chapters: []
    }

    // 基于大纲生成章节
    if (outline.chapters && outline.chapters.length > 0) {
      structure.chapters = outline.chapters.map((chapter, index) => ({
        id: index + 1,
        title: chapter.title || `第${index + 1}章`,
        description: chapter.description || '',
        scenes: this.generateFallbackScenesForChapter(chapter, index + 1, outline)
      }))
    } else {
      // 如果没有章节信息，生成默认章节
      structure.chapters = [
        {
          id: 1,
          title: "故事开端",
          description: "故事的开端，建立世界观和角色关系",
          scenes: this.generateFallbackScenesForChapter(null, 1, outline)
        },
        {
          id: 2,
          title: "冲突与选择",
          description: "主要冲突的展开，分支选择点",
          scenes: this.generateFallbackScenesForChapter(null, 2, outline)
        },
        {
          id: 3,
          title: "结局",
          description: "故事的高潮部分，通向不同的结局",
          scenes: this.generateFallbackScenesForChapter(null, 3, outline)
        }
      ]
    }

    return structure
  }

  // 为章节生成备用场景
  static generateFallbackScenesForChapter(chapter, chapterId, outline) {
    const scenes = []
    const sceneCount = chapter?.scenes || 3

    for (let i = 1; i <= sceneCount; i++) {
      const sceneId = `scene-${chapterId}-${i}`
      scenes.push({
        id: sceneId,
        title: chapter?.scenes?.[i-1]?.title || `第${chapterId}章 场景${i}`,
        description: chapter?.scenes?.[i-1]?.description || `这是第${chapterId}章的第${i}个场景`,
        content: this.generateFallbackSceneContent(sceneId, chapterId, i, outline),
        dialogues: this.generateFallbackDialogues(outline),
        choices: this.generateFallbackChoices(sceneId, chapterId, i),
        imagePrompt: `${outline.title}, chapter ${chapterId}, scene ${i}, ${outline.genre || 'fantasy'} style`,
        sceneImage: `https://picsum.photos/400/300?random=${chapterId * 10 + i}`,
        chapter: chapterId,
        scene: i,
        location: `第${chapterId}章场景${i}的地点`,
        mood: '紧张',
        timeOfDay: '下午',
        background: `第${chapterId}章场景${i}的背景描述`
      })
    }

    return scenes
  }

  // 生成备用镜头内容
  static generateFallbackSceneContent(sceneId, chapterId, sceneNum, outline) {
    return `在"${outline.title}"的第${chapterId}章第${sceneNum}个镜头中，故事继续展开。

${outline.summary || '这是一个充满冒险和选择的故事。'} 在这个关键时刻，你需要做出重要的决定，这将影响整个故事的走向。

环境氛围紧张而充满悬念，每一个选择都可能带来不同的结果。请仔细考虑你的每一个决定，因为它们将塑造你的命运。`
  }

  // 生成备用对话
  static generateFallbackDialogues(outline) {
    return [
      {
        character: "主角",
        content: "我需要仔细考虑这个选择...",
        emotion: "思考"
      },
      {
        character: "旁白",
        content: outline.summary || "故事正在展开，每一个选择都很重要。",
        emotion: "叙述"
      }
    ]
  }

  // 生成备用选择
  static generateFallbackChoices(sceneId, chapterId, sceneNum) {
    // 只在特定场景添加选择
    if (sceneId === 'scene-1-3' || sceneId === 'scene-2-2') {
      return [
        {
          id: `choice-${sceneId}-1`,
          text: "勇敢面对",
          targetNodeId: sceneId === 'scene-1-3' ? 'scene-2-1' : 'scene-3-1',
          path: "brave",
          consequence: "选择了勇敢的道路"
        },
        {
          id: `choice-${sceneId}-2`,
          text: "谨慎行事",
          targetNodeId: sceneId === 'scene-1-3' ? 'scene-2-2' : 'scene-3-2',
          path: "cautious",
          consequence: "选择了谨慎的道路"
        }
      ]
    }
    return []
  }

  // 生成章节场景 - 基于大纲创建场景
  static generateChapterScenes(chapter, structure, outline) {
    // 如果章节已经有场景（来自AI生成），直接返回
    if (chapter.scenes && chapter.scenes.length > 0) {
      return chapter.scenes
    }
    
    const scenes = []
    
    if (chapter.id === 1) {
      // 第一章：故事开端 (3个场景)
      scenes.push(...this.generateChapter1Scenes(outline))
    } else if (chapter.id === 2) {
      // 第二章：冲突与选择 (3个场景)
      scenes.push(...this.generateChapter2Scenes(outline))
    } else if (chapter.id === 3) {
      // 第三章：结局 (2个场景)
      scenes.push(...this.generateChapter3Scenes(outline))
    }
    
    return scenes
  }

    // 生成第一章场景 (3个场景)
  static generateChapter1Scenes(outline) {
    return [
      {
        id: "scene-1-1",
        title: outline.chapters?.[0]?.title || "故事开始",
        description: outline.chapters?.[0]?.description || "故事的开端",
        content: `基于你的创意"${outline.title}"，${outline.summary || '故事开始了'}。${outline.mainPlot || '你发现自己置身于一个充满可能性的世界中。'}

这是一个全新的开始，空气中弥漫着未知的气息。你环顾四周，发现自己站在一个神秘的地方，这里的一切都与你熟悉的世界不同。阳光透过云层洒下，在地面上投下斑驳的光影。

"欢迎来到这个充满奇迹的世界，"一个温和的声音在你身后响起。你转过身，看到一个身穿长袍的老者正微笑着看着你。"我是你的向导，将帮助你在这个世界中找到自己的道路。"

你感到既兴奋又紧张，因为你知道，从这一刻开始，你的每一个选择都将影响这个世界的命运。`,
        dialogues: [
          { character: "主角", content: "这就是开始..." },
          { character: "旁白", content: outline.summary || "故事正在展开" }
        ],
        choices: [],
        imagePrompt: `${outline.title}, story beginning, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=1",
        chapter: 1,
        scene: 1,
        location: outline.chapters?.[0]?.location || "故事开始的地点",
        mood: outline.chapters?.[0]?.mood || "神秘而充满期待",
        timeOfDay: "白天",
        background: outline.chapters?.[0]?.background || "故事背景正在建立"
      },
      {
        id: "scene-1-2",
        title: outline.chapters?.[0]?.scenes?.[1]?.title || "重要发现",
        description: outline.chapters?.[0]?.scenes?.[1]?.description || "发现改变命运的关键线索",
        content: outline.chapters?.[0]?.scenes?.[1]?.content || `在"${outline.title}"的世界中，你发现了一个重要的线索，这将引导你进入更深层的冒险。

你继续探索这个神秘的世界，突然，一道奇异的光芒吸引了你的注意。走近一看，你发现了一块古老的石碑，上面刻着神秘的符文。当你触摸石碑时，一股温暖的能量流遍全身，同时，你的脑海中浮现出一些模糊的记忆片段。

"这些符文...它们似乎在诉说着什么，"你自言自语道。石碑上的文字开始发光，仿佛在回应你的触摸。你意识到，这个发现将改变你对这个世界的理解。

就在这时，你听到了脚步声。有人正在接近...`,
        dialogues: [
          { character: "主角", content: "这很重要..." },
          { character: "旁白", content: outline.chapters?.[0]?.scenes?.[1]?.description || "命运正在改变" }
        ],
        choices: [],
        imagePrompt: `${outline.title}, important discovery, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=2",
        chapter: 1,
        scene: 2,
        location: outline.chapters?.[0]?.scenes?.[1]?.location || "发现地点",
        mood: outline.chapters?.[0]?.scenes?.[1]?.mood || "好奇与紧张",
        timeOfDay: "黄昏",
        background: outline.chapters?.[0]?.scenes?.[1]?.background || "重要线索散发着神秘的光芒"
      },
      {
        id: "scene-1-3",
        title: outline.chapters?.[0]?.scenes?.[2]?.title || "第一个选择",
        description: outline.chapters?.[0]?.scenes?.[2]?.description || "面临第一个重要的选择",
        content: outline.chapters?.[0]?.scenes?.[2]?.content || `在"${outline.title}"的故事中，你面临第一个重要选择。${outline.branches?.[0]?.description || '这将决定你接下来的道路。'}`,
        dialogues: [
          { character: "引导者", content: outline.branches?.[0]?.dialogue || "现在你必须选择：接受使命还是回到平凡的生活？" },
          { character: "主角", content: "如果我接受，会面临什么？" },
          { character: "引导者", content: "危险、挑战，但也可能是伟大的冒险。" }
        ],
        choices: outline.branches?.[0]?.choices || [
          {
            id: "choice-1-3-1",
            text: "勇敢面对",
            targetNodeId: "scene-2-1",
            path: "brave",
            consequence: "你选择了勇敢面对挑战，开始了英雄之路"
          },
          {
            id: "choice-1-3-2",
            text: "谨慎行事",
            targetNodeId: "scene-2-2",
            path: "cautious",
            consequence: "你选择了谨慎行事，先了解更多信息"
          }
        ],
        imagePrompt: `${outline.title}, important choice, crossroads, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=3",
        chapter: 1,
        scene: 3,
        location: "选择之地",
        mood: "紧张与期待",
        timeOfDay: "深夜",
        background: "命运的分叉路口，选择将决定未来的道路"
      }
    ]
  }

  // 生成第二章场景 (3个场景)
  static generateChapter2Scenes(outline) {
    return [
      {
        id: "scene-2-1",
        title: outline.chapters?.[1]?.scenes?.[0]?.title || "勇敢之路",
        description: outline.chapters?.[1]?.scenes?.[0]?.description || "选择勇敢面对挑战后的发展",
        content: outline.chapters?.[1]?.scenes?.[0]?.content || `在"${outline.title}"的故事中，你选择了勇敢面对挑战。${outline.mainPlot || '前方等待着你的将是严峻的考验。'}`,
        dialogues: [
          { character: "主角", content: "我不会退缩，无论面对什么困难。" },
          { character: "挑战者", content: outline.chapters?.[1]?.scenes?.[0]?.dialogue || "勇气可嘉，但光有勇气是不够的。" }
        ],
        choices: outline.chapters?.[1]?.scenes?.[0]?.choices || [
          {
            id: "choice-2-1-1",
            text: "继续勇敢前行",
            targetNodeId: "scene-2-3",
            path: "brave-continue",
            consequence: "你选择继续勇敢前行，来到汇聚点"
          }
        ],
        imagePrompt: `${outline.title}, brave hero, facing challenges, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=4",
        chapter: 2,
        scene: 1,
        location: outline.chapters?.[1]?.scenes?.[0]?.location || "挑战之地",
        mood: outline.chapters?.[1]?.scenes?.[0]?.mood || "勇敢与紧张",
        timeOfDay: "白天",
        background: outline.chapters?.[1]?.scenes?.[0]?.background || "充满挑战的环境，英雄的试炼场"
      },
      {
        id: "scene-2-2",
        title: outline.chapters?.[1]?.scenes?.[1]?.title || "谨慎的观察",
        description: outline.chapters?.[1]?.scenes?.[1]?.description || "选择谨慎行事后的发展",
        content: outline.chapters?.[1]?.scenes?.[1]?.content || `在"${outline.title}"的故事中，你选择了谨慎行事。${outline.mainPlot || '这种策略让你避免了不必要的风险。'}`,
        dialogues: [
          { character: "主角", content: "我需要了解更多信息才能做出正确的决定。" },
          { character: "智者", content: outline.chapters?.[1]?.scenes?.[1]?.dialogue || "明智的选择，知识就是力量。" }
        ],
        choices: outline.chapters?.[1]?.scenes?.[1]?.choices || [
          {
            id: "choice-2-2-1",
            text: "坚持原则",
            targetNodeId: "scene-3-1",
            path: "justice",
            consequence: "你选择了坚持正义，即使面临困难"
          },
          {
            id: "choice-2-2-2",
            text: "寻求和解",
            targetNodeId: "scene-3-2",
            path: "peace",
            consequence: "你选择了和解，寻求和平的解决方案"
          }
        ],
        imagePrompt: `${outline.title}, careful observation, strategic thinking, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=5",
        chapter: 2,
        scene: 2,
        location: outline.chapters?.[1]?.scenes?.[1]?.location || "观察点",
        mood: outline.chapters?.[1]?.scenes?.[1]?.mood || "谨慎与思考",
        timeOfDay: "黄昏",
        background: outline.chapters?.[1]?.scenes?.[1]?.background || "观察和思考的地方，策略的制定地"
      },
      {
        id: "scene-2-3",
        title: outline.chapters?.[1]?.scenes?.[2]?.title || "汇聚点",
        description: outline.chapters?.[1]?.scenes?.[2]?.description || "不同路径的汇聚",
        content: outline.chapters?.[1]?.scenes?.[2]?.content || `在"${outline.title}"的故事中，无论你选择了什么路径，最终都会来到这里。${outline.mainPlot || '这是所有选择的汇聚点。'}`,
        dialogues: [
          { character: "主角", content: "看来所有的选择都指向了这里。" },
          { character: "命运", content: outline.chapters?.[1]?.scenes?.[2]?.dialogue || "这就是你的命运，无论你如何选择。" }
        ],
        choices: outline.chapters?.[1]?.scenes?.[2]?.choices || [
          {
            id: "choice-2-3-1",
            text: "坚持正义",
            targetNodeId: "scene-3-1",
            path: "justice",
            consequence: "你选择了坚持正义，走向正义的胜利"
          },
          {
            id: "choice-2-3-2",
            text: "寻求和解",
            targetNodeId: "scene-3-2",
            path: "peace",
            consequence: "你选择了寻求和解，走向和平的结局"
          }
        ],
        imagePrompt: `${outline.title}, convergence point, multiple paths meeting, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=6",
        chapter: 2,
        scene: 3,
        location: outline.chapters?.[1]?.scenes?.[2]?.location || "命运交汇点",
        mood: outline.chapters?.[1]?.scenes?.[2]?.mood || "庄严与神秘",
        timeOfDay: "夜晚",
        background: outline.chapters?.[1]?.scenes?.[2]?.background || "所有道路的终点，命运的汇聚地"
      }
    ]
  }

  // 生成第三章场景 (2个场景)
  static generateChapter3Scenes(outline) {
    return [
      {
        id: "scene-3-1",
        title: outline.chapters?.[2]?.scenes?.[0]?.title || "正义的胜利",
        description: outline.chapters?.[2]?.scenes?.[0]?.description || "坚持原则的结局",
        content: outline.chapters?.[2]?.scenes?.[0]?.content || `在"${outline.title}"的故事中，你选择了坚持原则，最终取得了正义的胜利。${outline.mainPlot || '这是通过坚持和勇气获得的胜利。'}`,
        dialogues: [
          { character: "主角", content: "正义最终会战胜邪恶！" },
          { character: "正义使者", content: outline.chapters?.[2]?.scenes?.[0]?.dialogue || "你的坚持让我看到了希望。" }
        ],
        choices: [],
        imagePrompt: `${outline.title}, justice victory, moral triumph, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=7",
        chapter: 3,
        scene: 1,
        location: outline.chapters?.[2]?.scenes?.[0]?.location || "正义殿堂",
        mood: outline.chapters?.[2]?.scenes?.[0]?.mood || "胜利与正义",
        timeOfDay: "黎明",
        background: outline.chapters?.[2]?.scenes?.[0]?.background || "正义的曙光，胜利的见证"
      },
      {
        id: "scene-3-2",
        title: outline.chapters?.[2]?.scenes?.[1]?.title || "和平的结局",
        description: outline.chapters?.[2]?.scenes?.[1]?.description || "寻求和解的结局",
        content: outline.chapters?.[2]?.scenes?.[1]?.content || `在"${outline.title}"的故事中，你选择了寻求和解，最终实现了和平。${outline.mainPlot || '这是通过理解和宽容获得的胜利。'}`,
        dialogues: [
          { character: "主角", content: "和平比胜利更重要。" },
          { character: "和平使者", content: outline.chapters?.[2]?.scenes?.[1]?.dialogue || "你的宽容创造了奇迹。" }
        ],
        choices: [],
        imagePrompt: `${outline.title}, peace ending, reconciliation, ${outline.genre || 'fantasy'} style`,
        sceneImage: "https://picsum.photos/400/300?random=8",
        chapter: 3,
        scene: 2,
        location: outline.chapters?.[2]?.scenes?.[1]?.location || "和平圣地",
        mood: outline.chapters?.[2]?.scenes?.[1]?.mood || "和平与和谐",
        timeOfDay: "黄昏",
        background: outline.chapters?.[2]?.scenes?.[1]?.background || "和平的象征，和谐的见证"
      }
    ]
  }

  // 生成详细角色信息
  static async generateDetailedCharacters(characterList, content, genre, onProgress) {
    // 检查是否配置了语言模型API
    const languageConfig = settingsService.getLanguageModelConfig()
    
    if (languageConfig.apiKey && languageConfig.apiUrl) {
      console.log('✅ 语言模型API已配置，使用真实API生成角色详情...')
      try {
        // 启动进度动画
        const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成角色信息...")
        
        const result = await RealAIService.generateCharacterInfo(characterList, content, genre)
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(100, "角色信息生成完成！")
        console.log('✅ 真实API生成角色详情成功')
        return result
      } catch (error) {
        console.error('❌ 真实API调用失败:', error)
        // API配置成功但调用失败，使用基于大纲的备用方案
        console.log('🔄 使用基于大纲的角色生成备用方案...')
        return this.generateFallbackCharacters(characterList, content, genre)
      }
    }
    
    console.log('⚠️ 语言模型API未配置，使用模拟数据生成角色详情...')
    
    // 启动进度动画
    const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成角色信息...")
    
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // 停止进度动画
    clearInterval(progressInterval)
    
    onProgress && onProgress(100, "角色信息生成完成！")
    
    // 基于用户输入创建角色列表
    const characters = [
      {
        id: "char-1",
        name: "主角",
        role: "protagonist",
        description: `基于你的创意"${content}"，这是故事的主要角色，玩家将扮演这个角色`,
        appearance: "年轻有为，眼神坚定，穿着现代服装但透露出不凡的气质",
        background: `在"${content}"的故事中，一个普通的年轻人，突然被命运选中成为英雄`,
        personality: "勇敢、正义、有同情心，但有时会犹豫不决",
        relationships: "与其他角色之间的关系将在故事发展中逐渐揭示",
        stats: { intelligence: 75, strength: 70, charisma: 80, wisdom: 65 },
        imagePrompt: `protagonist from "${content}", determined expression, ${genre} style`,
        imageUrl: "https://picsum.photos/200/200?random=10"
      },
      {
        id: "char-2",
        name: "神秘信使",
        role: "mentor",
        description: "引导主角的神秘人物",
        appearance: "身披斗篷，面容模糊，散发着神秘的气息",
        background: "来自命运之地的使者，负责引导被选中的英雄",
        personality: "智慧、神秘、耐心，但不会直接给出答案",
        relationships: "是主角的引路人，但不会干涉主角的选择",
        stats: { intelligence: 90, strength: 60, charisma: 70, wisdom: 95 },
        imagePrompt: "mysterious messenger, hooded figure, mystical aura",
        imageUrl: "https://picsum.photos/200/200?random=11"
      },
      {
        id: "char-3",
        name: "光明守护者",
        role: "ally",
        description: "光明之路的守护者",
        appearance: "身穿白色长袍，面容慈祥，散发着温暖的光芒",
        background: "守护正义和光明的古老存在，拥有强大的保护力量",
        personality: "正义、仁慈、坚定，但有时过于理想化",
        relationships: "是光明之路的指引者，会全力支持主角",
        stats: { intelligence: 85, strength: 80, charisma: 90, wisdom: 85 },
        imagePrompt: "light guardian, white robes, benevolent expression, protective aura",
        imageUrl: "https://picsum.photos/200/200?random=12"
      },
      {
        id: "char-4",
        name: "黑暗领主",
        role: "antagonist",
        description: "黑暗之路的统治者",
        appearance: "身穿黑色铠甲，面容英俊但眼神冷酷，散发着强大的威压",
        background: "统治黑暗领域的强大存在，拥有毁灭性的力量",
        personality: "野心勃勃、聪明、有魅力，但缺乏道德底线",
        relationships: "是黑暗之路的引导者，会利用主角的欲望",
        stats: { intelligence: 90, strength: 95, charisma: 85, wisdom: 80 },
        imagePrompt: "dark lord, black armor, handsome but cold, menacing presence",
        imageUrl: "https://picsum.photos/200/200?random=13"
      },
      {
        id: "char-5",
        name: "命运女神",
        role: "deity",
        description: "掌控命运的神明",
        appearance: "美丽而神秘，眼中闪烁着星辰的光芒，散发着神圣的气息",
        background: "宇宙中掌控命运的最高存在，见证所有生命的选择",
        personality: "公正、智慧、神秘，不会干涉但会给予指引",
        relationships: "是命运的见证者，会帮助主角理解选择的意义",
        stats: { intelligence: 100, strength: 100, charisma: 100, wisdom: 100 },
        imagePrompt: "fate goddess, beautiful and mysterious, starry eyes, divine presence",
        imageUrl: "https://picsum.photos/200/200?random=14"
      }
    ]
    
    return characters
  }

  // 生成备用角色信息 - 当API调用失败时使用
  static generateFallbackCharacters(characterList, content, genre) {
    console.log('🔄 生成备用角色信息，基于内容:', content.substring(0, 50) + '...')
    
    // 基于内容和大纲生成角色
    const characters = []
    
    // 生成主角
    characters.push({
      id: "char-1",
      name: "主角",
      role: "protagonist",
      description: `基于"${content}"故事的主角，玩家将扮演这个角色`,
      appearance: "年轻有为，眼神坚定，穿着符合故事背景的服装",
      background: `在"${content}"的世界中，一个普通的年轻人，突然被命运选中成为关键人物`,
      personality: "勇敢、正义、有同情心，但有时会犹豫不决",
      relationships: "与其他角色之间的关系将在故事发展中逐渐揭示",
      stats: { intelligence: 75, strength: 70, charisma: 80, wisdom: 65 },
      imagePrompt: `protagonist from "${content}", determined expression, ${genre} style`,
      imageUrl: "https://picsum.photos/200/200?random=10",
      languageStyle: "普通",
      isActive: true,
      firstAppearance: "故事开始",
      lastAppearance: "故事结束",
      dialogueCount: 0
    })
    
    // 基于大纲生成其他角色
    if (characterList && characterList.length > 0) {
      characterList.forEach((char, index) => {
        if (index < 4) { // 最多生成4个角色
          characters.push({
            id: `char-${index + 2}`,
            name: char.name || `角色${index + 1}`,
            role: char.role || "supporting",
            description: char.description || `在"${content}"故事中的重要角色`,
            appearance: char.appearance || "神秘而引人注目",
            background: char.background || `与主角命运交织的角色`,
            personality: char.personality || "复杂而多面",
            relationships: char.relationships || "与主角有重要联系",
            stats: { intelligence: 70, strength: 60, charisma: 75, wisdom: 70 },
            imagePrompt: `${char.name || `character${index + 1}`} from "${content}", ${genre} style`,
            imageUrl: `https://picsum.photos/200/200?random=${11 + index}`,
            languageStyle: "普通",
            isActive: true,
            firstAppearance: "故事中段",
            lastAppearance: "故事结束",
            dialogueCount: 0
          })
        }
      })
    } else {
      // 如果没有角色列表，生成默认角色
      characters.push({
        id: "char-2",
        name: "神秘导师",
        role: "mentor",
        description: "引导主角的神秘人物",
        appearance: "身披斗篷，面容模糊，散发着神秘的气息",
        background: "来自命运之地的使者，负责引导被选中的英雄",
        personality: "智慧、神秘、耐心，但不会直接给出答案",
        relationships: "是主角的引路人，但不会干涉主角的选择",
        stats: { intelligence: 90, strength: 60, charisma: 70, wisdom: 95 },
        imagePrompt: "mysterious mentor, hooded figure, mystical aura",
        imageUrl: "https://picsum.photos/200/200?random=11",
        languageStyle: "神秘",
        isActive: true,
        firstAppearance: "故事开始",
        lastAppearance: "故事中段",
        dialogueCount: 0
      })
    }
    
    return characters
  }

  // 生成所有镜头内容 - 简化版本
  static async generateAllScenes(storyStructure, characters, style, onProgress) {
    console.log('开始生成镜头内容...', { chapters: storyStructure.chapters.length, characters: characters.length })
    
    // 检查是否配置了语言模型API
    const languageConfig = settingsService.getLanguageModelConfig()
    
    const allScenes = []
    
    if (languageConfig.apiKey && languageConfig.apiUrl) {
      console.log('✅ 语言模型API已配置，批量生成场景内容...')
      try {
        // 启动进度动画
        const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成场景内容...")
        
        // 批量生成所有场景内容，减少API调用次数
        const detailedScenes = await RealAIService.generateAllScenesContent(storyStructure, characters, style)
        allScenes.push(...detailedScenes)
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(100, "场景内容生成完成！")
      } catch (error) {
        console.error('❌ 批量场景内容生成失败:', error)
        // API配置成功但调用失败，使用备用方案
        console.log('🔄 使用备用场景内容生成方案...')
        
        // 启动进度动画
        const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成场景内容...")
        
        for (const chapter of storyStructure.chapters) {
          for (const scene of chapter.scenes) {
            allScenes.push(this.enhanceSceneContent(scene, characters, style))
          }
        }
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(100, "场景内容生成完成！")
      }
    } else {
      console.log('⚠️ 语言模型API未配置，使用模拟数据增强场景内容...')
      
      // 启动进度动画
      const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成场景内容...")
      
      for (const chapter of storyStructure.chapters) {
        for (const scene of chapter.scenes) {
          allScenes.push(this.enhanceSceneContent(scene, characters, style))
        }
      }
      
      // 停止进度动画
      clearInterval(progressInterval)
      
      onProgress && onProgress(100, "场景内容生成完成！")
    }
    
    console.log('所有场景内容生成完成，总数:', allScenes.length)
    return allScenes
  }

  // 增强场景内容 - 为场景添加详细内容
  static enhanceSceneContent(scene, characters, style) {
    const enhancedScene = {
      ...scene,
      // 确保有详细的内容
      content: scene.content || this.generateSceneContent(scene, characters, style),
      description: scene.description || this.generateSceneDescription(scene, characters, style),
      dialogues: scene.dialogues || this.generateSceneDialogues(scene, characters, style),
      // 确保有图像提示词
      imagePrompt: scene.imagePrompt || this.generateImagePrompt(scene, characters, style),
      // 确保有背景信息
      background: scene.background || this.generateBackground(scene, characters, style),
      mood: scene.mood || this.generateMood(scene, characters, style),
      timeOfDay: scene.timeOfDay || this.generateTimeOfDay(scene, characters, style),
      location: scene.location || this.generateLocation(scene, characters, style)
    }
    
    return enhancedScene
  }

  // 生成场景内容
  static generateSceneContent(scene, characters, style) {
    const characterNames = characters ? characters.map(c => c.name).join('、') : '主角'
    return `在${scene.title}中，${characterNames}面临着重要的选择。${scene.description || '这是一个关键的转折点，需要仔细考虑每一个选择。'}`
  }

  // 生成场景描述
  static generateSceneDescription(scene, characters, style) {
    return `这是${scene.title}的详细描述，包含了丰富的环境细节和角色互动。`
  }

  // 生成场景对话
  static generateSceneDialogues(scene, characters, style) {
    if (!characters || characters.length === 0) return []
    
    const dialogues = []
    const mainCharacter = characters[0]
    
    // 为主角生成一些思考对话
    dialogues.push({
      character: mainCharacter.name,
      content: `在${scene.title}中，我需要仔细考虑每一个选择...`,
      emotion: '思考'
    })
    
    // 如果有其他角色，生成一些互动对话
    if (characters.length > 1) {
      const otherCharacter = characters[1]
      dialogues.push({
        character: otherCharacter.name,
        content: `这是一个重要的时刻，你的选择将影响整个故事的走向。`,
        emotion: '严肃'
      })
    }
    
    return dialogues
  }

  // 生成图像提示词
  static generateImagePrompt(scene, characters, style) {
    const characterNames = characters ? characters.map(c => c.name).join(', ') : 'protagonist'
    return `${scene.title}, ${characterNames}, ${style} style, cinematic composition, dramatic lighting, high quality`
  }

  // 生成背景信息
  static generateBackground(scene, characters, style) {
    return `在${scene.title}的背景中，环境氛围紧张而充满悬念。`
  }

  // 生成情绪
  static generateMood(scene, characters, style) {
    const moods = ['紧张', '神秘', '激动', '沉思', '期待']
    return moods[Math.floor(Math.random() * moods.length)]
  }

  // 生成时间
  static generateTimeOfDay(scene, characters, style) {
    const times = ['清晨', '中午', '下午', '傍晚', '夜晚']
    return times[Math.floor(Math.random() * times.length)]
  }

  // 生成地点
  static generateLocation(scene, characters, style) {
    return `在${scene.title}的场景中`
  }

  // 生成场景图片 - 独立判断图像生成API配置
  static async generateSceneImages(scenes, characters, style) {
    console.log('开始生成场景图片...', { scenesCount: scenes.length })
    
    const imageConfig = settingsService.getImageGenerationConfig()
    const doubaoConfig = settingsService.getDoubaoImageConfig()
    
    // 如果当前选择的图像模型是豆包，优先使用豆包
    if (imageConfig.model === 'doubao-seedream-4-0' && imageConfig.apiKey && imageConfig.apiUrl) {
      console.log('✅ 豆包-seedream-4.0已配置，使用豆包API生成场景图片...')
      try {
        // 使用豆包API批量生成图片
        const scenesWithImages = await DoubaoImageServiceV2.generateBatchSceneImages(scenes, style, characters, {
          size: '2K'
        })
        console.log('✅ 豆包API批量图片生成完成')
        return scenesWithImages
      } catch (error) {
        console.error('❌ 豆包API调用失败:', error)
        // 回退到其他图像生成API
        if (imageConfig.apiKey && imageConfig.apiUrl) {
          console.log('🔄 回退到其他图像生成API...')
          return await this.generateSceneImagesWithFallback(scenes, characters, style, imageConfig)
        } else {
          throw new Error(`图像生成失败: ${error.message}`)
        }
      }
    } else if (imageConfig.apiKey && imageConfig.apiUrl) {
      return await this.generateSceneImagesWithFallback(scenes, characters, style, imageConfig)
    } else {
      console.log('⚠️ 图像生成API未配置，使用模拟图片')
      // 场景已经包含了模拟图片URL，直接返回
      return scenes
    }
  }

  // 使用备用图像生成API
  static async generateSceneImagesWithFallback(scenes, characters, style, imageConfig) {
    console.log('✅ 其他图像生成API已配置，使用真实API生成场景图片...')
    try {
      // 为每个场景生成真实图片
      for (let i = 0; i < scenes.length; i++) {
        const scene = scenes[i]
        if (scene.imagePrompt) {
          try {
            const imageUrl = await RealAIService.generateSceneImage(
              scene.imagePrompt, 
              characters, 
              style
            )
            scene.sceneImage = imageUrl
            console.log(`✅ 场景 ${i + 1} 图片生成完成`)
          } catch (error) {
            console.error(`❌ 场景 ${i + 1} 图片生成失败:`, error)
            // API配置成功但调用失败，保持空图片状态而不是使用模拟图片
            scene.sceneImage = ''
            console.warn(`场景 ${i + 1} 图片生成失败，将显示为无图片状态`)
          }
        }
      }
      return scenes
    } catch (error) {
      console.error('❌ 图像生成API调用失败:', error)
      // API配置成功但调用失败，抛出错误而不是回退到模拟数据
      throw new Error(`图像生成失败: ${error.message}`)
    }
  }

  // 构建故事连接 - 简化版本
  static async buildStoryConnections(scenes) {
    const connections = []
    
    // 创建场景间的连接
    for (let i = 0; i < scenes.length - 1; i++) {
      const currentScene = scenes[i]
      const nextScene = scenes[i + 1]
      
      connections.push({
        id: `scene-conn-${i}`,
        fromSceneId: currentScene.id,
        toSceneId: nextScene.id,
        type: 'sequential',
        description: `${currentScene.title} 到 ${nextScene.title} 的剧情连接`
      })
    }
    
    return connections
  }

  // 完成故事整合
  static async finalizeStory(outline, characters, scenes, connections, storyStructure, onProgress) {
    console.log('🔧 开始整合故事数据...')
    console.log('📊 输入数据:', {
      outline: outline?.title || '无标题',
      charactersCount: characters?.length || 0,
      scenesCount: scenes?.length || 0,
      connectionsCount: connections?.length || 0,
      storyStructure: storyStructure ? '有' : '无'
    })
    
    // 启动进度动画
    const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在完成最终故事...")
    
    // 确保章节数据正确传递
    const chapters = storyStructure?.chapters || outline?.chapters || []
    console.log('📚 章节数据:', chapters.length, '个章节')
    
    // 确保场景数据正确传递
    const allScenes = scenes || []
    console.log('🎬 场景数据:', allScenes.length, '个场景')
    
    // 如果场景数据为空，尝试从章节中提取
    if (allScenes.length === 0 && chapters.length > 0) {
      console.log('⚠️ 场景数据为空，从章节中提取...')
      const extractedScenes = chapters.flatMap(chapter => chapter.scenes || [])
      console.log('🔍 从章节提取的场景数:', extractedScenes.length)
      allScenes.push(...extractedScenes)
    }
    
    const completeStory = {
      id: `story-${Date.now()}`,
      metadata: {
        title: outline?.title || storyStructure?.title || 'AI创作的故事',
        summary: outline?.summary || storyStructure?.summary || '这是一个精彩的故事',
        genre: outline?.genre || '冒险',
        style: outline?.style || '现代',
        createdAt: new Date().toISOString(),
        version: '1.0'
      },
      outline: outline || {
        title: storyStructure?.title || 'AI创作的故事',
        summary: storyStructure?.summary || '这是一个精彩的故事',
        mainPlot: storyStructure?.mainPlot || '主角将面临各种挑战和选择'
      },
      characters: characters || [],
      scenes: allScenes,
      chapters: chapters,
      connections: connections || [],
      structure: {
        chapters: chapters,
        branches: storyStructure?.branches || outline?.branches || [],
        mainPlot: outline?.mainPlot || storyStructure?.mainPlot || '主角将面临各种挑战和选择'
      },
      stats: {
        totalScenes: allScenes.length,
        totalCharacters: characters?.length || 0,
        totalChoices: allScenes.reduce((acc, scene) => acc + (scene.choices?.length || 0), 0),
        totalBranches: storyStructure?.branches?.length || outline?.branches?.length || 0,
        totalEndings: 2, // 2个不同结局
        estimatedPlayTime: `${Math.ceil(allScenes.length * 2)} 分钟`,
        complexity: "中", // 中等复杂度互动内容
        replayability: "高" // 高重玩价值
      }
    }
    
    console.log('✅ 故事数据整合完成:', {
      title: completeStory.metadata.title,
      chapters: completeStory.chapters.length,
      scenes: completeStory.scenes.length,
      characters: completeStory.characters.length
    })
    
    // 停止进度动画
    clearInterval(progressInterval)
    
    onProgress && onProgress(100, "故事创作完成！")
    
    return completeStory
  }

  // AI服务模块 - 基于API配置决定使用真实数据还是模拟数据
  static async generateStoryOutline(content, genre, style, onProgress) {
    // 检查是否配置了语言模型API
    const languageConfig = settingsService.getLanguageModelConfig()
    
    if (languageConfig.apiKey && languageConfig.apiUrl) {
      console.log('✅ 语言模型API已配置，使用真实API生成故事大纲...')
      try {
        // 启动进度动画
        const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成故事大纲...")
        
        const result = await RealAIService.generateStoryOutline(content, genre, style)
        
        // 停止进度动画
        clearInterval(progressInterval)
        
        onProgress && onProgress(100, "故事大纲生成完成！")
        console.log('✅ 真实API生成成功:', result.title)
        return result
      } catch (error) {
        console.error('❌ 真实API调用失败:', error)
        // API配置成功但调用失败，抛出错误而不是回退到模拟数据
        throw new Error(`故事大纲生成失败: ${error.message}`)
      }
    }
    
    console.log('⚠️ 语言模型API未配置，使用模拟数据生成故事大纲...')
    
    // 启动进度动画
    const progressInterval = this.startProgressAnimation(0, 90, onProgress, "正在生成故事大纲...")
    
    // 模拟AI处理延迟
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // 停止进度动画
    clearInterval(progressInterval)
    
    onProgress && onProgress(100, "故事大纲生成完成！")
    
    // 返回简化的故事大纲
    const outline = {
      title: this.generateTitle(content),
      summary: this.generateSummary(content, genre),
      chapters: this.generateChapters(content, genre),
      characters: this.generateCharacters(content, genre),
      mainPlot: this.generateMainPlot(content, genre),
      branches: this.generateBranches(content, genre),
      genre: genre,
      style: style
    }
    
    return outline
  }
  
  // 生成角色信息
  static async generateCharacterInfo(characterName, storyContext, genre) {
    // 检查是否配置了语言模型API
    const languageConfig = settingsService.getLanguageModelConfig()
    
    if (languageConfig.apiKey && languageConfig.apiUrl) {
      console.log('✅ 语言模型API已配置，使用真实API生成角色信息...')
      try {
        const result = await RealAIService.generateCharacterInfo(characterName, storyContext, genre)
        console.log('✅ 真实API角色生成成功:', characterName)
        return result
      } catch (error) {
        console.error('❌ 真实API调用失败:', error)
        // API配置成功但调用失败，抛出错误而不是回退到模拟数据
        throw new Error(`角色信息生成失败: ${error.message}`)
      }
    }
    
    console.log('⚠️ 语言模型API未配置，使用模拟数据生成角色信息...')
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    return {
      name: characterName,
      appearance: this.generateAppearance(characterName, genre),
      background: this.generateCharacterBackground(characterName, storyContext, genre),
      personality: this.generatePersonality(characterName, genre),
      relationships: this.generateRelationships(characterName, storyContext)
    }
  }
  
  // 生成场景图像提示词
  static async generateImagePrompt(sceneDescription, template, sceneIndex) {
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 返回基本的图片提示词
    const basePrompt = `${sceneDescription}, 高质量渲染, 电影级画面, 神秘奇幻风格`
    
    return {
      prompt: basePrompt,
      style: "fantasy",
      quality: 'high',
      aspectRatio: '16:9'
    }
  }
  
  // 续写章节
  static async continueChapter(endingContext, continuationPrompt, storyStyle) {
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    return {
      newChapter: {
        title: this.generateChapterTitle(endingContext),
        description: this.generateChapterDescription(endingContext, continuationPrompt),
        scenes: this.generateNewScenes(endingContext, continuationPrompt, storyStyle),
        connections: this.generateChapterConnections(endingContext)
      }
    }
  }
  
  // 辅助方法
  static generateTitle(content) {
    const titles = [
      "穿越时空的冒险",
      "命运的交汇点",
      "古代与现代的碰撞",
      "英雄的觉醒",
      "历史的转折点"
    ]
    return titles[Math.floor(Math.random() * titles.length)]
  }
  
  static generateSummary(content, genre) {
    return `基于您提供的故事内容"${content.substring(0, 50)}..."，AI将创建一个${genre}风格的互动故事。故事将包含多个分支剧情和结局，让玩家体验不同的故事路径。`
  }
  
  static generateChapters(content, genre) {
    return [
      {
        id: 1,
        title: "英雄的觉醒",
        description: "一个包含分支选择和多重结局的完整故事",
        scenes: 5
      }
    ]
  }
  
  static generateCharacters(content, genre) {
    return [
      {
        name: "主角",
        role: "protagonist",
        description: "故事的主要角色，玩家将扮演这个角色"
      },
      {
        name: "神秘信使",
        role: "mentor",
        description: "引导主角的神秘人物"
      },
      {
        name: "光明守护者",
        role: "ally",
        description: "光明之路的守护者"
      },
      {
        name: "黑暗领主",
        role: "antagonist",
        description: "黑暗之路的统治者"
      },
      {
        name: "命运女神",
        role: "deity",
        description: "掌控命运的神明"
      }
    ]
  }
  
  static generateMainPlot(content, genre) {
    return `故事将围绕"${content.substring(0, 30)}..."展开，包含多个关键决策点，每个选择都会影响故事的发展方向和最终结局。`
  }
  
  static generateBranches(content, genre) {
    return [
      {
        type: "moral",
        description: "道德选择分支",
        outcomes: ["光明路线", "黑暗路线", "平衡路线"]
      }
    ]
  }
  
  static generateAppearance(characterName, genre) {
    const appearances = {
      "主角": "年轻有为，眼神坚定，穿着现代服装但透露出不凡的气质",
      "神秘信使": "身披斗篷，面容模糊，散发着神秘的气息",
      "光明守护者": "身穿白色长袍，面容慈祥，散发着温暖的光芒",
      "黑暗领主": "身穿黑色铠甲，面容英俊但眼神冷酷，散发着强大的威压",
      "命运女神": "美丽而神秘，眼中闪烁着星辰的光芒，散发着神圣的气息"
    }
    
    return appearances[characterName] || "外表特征需要根据具体角色设定"
  }
  
  static generateCharacterBackground(characterName, storyContext, genre) {
    return `${characterName}的背景故事将与"${storyContext.substring(0, 20)}..."紧密相连，在${genre}的世界观中扮演重要角色。`
  }
  
  static generatePersonality(characterName, genre) {
    const personalities = {
      "主角": "勇敢、正义、有同情心，但有时会犹豫不决",
      "神秘信使": "智慧、神秘、耐心，但不会直接给出答案",
      "光明守护者": "正义、仁慈、坚定，但有时过于理想化",
      "黑暗领主": "野心勃勃、聪明、有魅力，但缺乏道德底线",
      "命运女神": "公正、智慧、神秘，不会干涉但会给予指引"
    }
    
    return personalities[characterName] || "性格特征需要根据具体角色设定"
  }
  
  static generateRelationships(characterName, storyContext) {
    return `${characterName}与其他角色之间的关系将在故事发展中逐渐揭示，形成复杂的人物关系网络。`
  }
  
  static translateToEnglish(sceneDescription, characterDescriptions, style) {
    // 这里应该是真实的翻译API调用
    // 目前返回模拟的英文提示词
    const sceneText = typeof sceneDescription === 'string' ? sceneDescription.substring(0, 100) : 'scene'
    const charText = typeof characterDescriptions === 'string' ? characterDescriptions.substring(0, 50) : 'characters'
    return `In ${style} style, ${sceneText}... featuring characters with ${charText}... dramatic lighting, cinematic composition.`
  }
  
  static generateSceneDescription(sceneContext, characters) {
    if (!characters || characters.length === 0) {
      return `在这个场景中，${sceneContext}。主要角色将面临重要的选择。`
    }
    return `在这个场景中，${sceneContext}。主要角色${characters.map(c => c.name).join('、')}将面临重要的选择。`
  }
  
  static generateDialogues(sceneContext, characters) {
    if (!characters || characters.length === 0) {
      return []
    }
    return characters.map(character => ({
      character: character.name,
      text: `作为${character.name}，在这个场景中我想说："${sceneContext.substring(0, 30)}..."`,
      emotion: "neutral"
    }))
  }
  
  static generateChoices(sceneContext, previousScenes) {
    return [
      {
        text: "选择A：继续前进",
        consequence: "可能导致正面结果",
        nextScene: "下一场景"
      },
      {
        text: "选择B：谨慎行事",
        consequence: "可能避免危险",
        nextScene: "安全场景"
      },
      {
        text: "选择C：寻求帮助",
        consequence: "可能获得支持",
        nextScene: "帮助场景"
      }
    ]
  }
  
  static generateSceneContextBackground(sceneContext) {
    // 修复：确保 sceneContext 是字符串类型
    if (typeof sceneContext !== 'string') {
      return "场景背景将完美配合剧情发展，营造相应的氛围和情绪。"
    }
    return `场景背景将完美配合"${sceneContext.substring(0, 40)}..."的剧情发展，营造相应的氛围和情绪。`
  }
  
  static generateChapterTitle(endingContext) {
    return `续写：${endingContext.substring(0, 20)}...的新篇章`
  }
  
  static generateChapterDescription(endingContext, continuationPrompt) {
    return `基于"${endingContext.substring(0, 30)}..."的结局，继续讲述"${continuationPrompt.substring(0, 40)}..."的故事。`
  }
  
  static generateNewScenes(endingContext, continuationPrompt, storyStyle) {
    return [
      {
        title: "新的开始",
        description: "在原有结局基础上的新起点",
        content: continuationPrompt
      },
      {
        title: "情节发展",
        description: "新章节的主要情节",
        content: "故事将如何继续发展"
      },
      {
        title: "新的结局",
        description: "新章节的结局",
        content: "可能的新结局方向"
      }
    ]
  }
  
  static generateChapterConnections(endingContext) {
    return {
      fromEnding: endingContext,
      toNewChapter: "新章节将直接连接到此结局",
      continuity: "保持故事逻辑的连贯性"
    }
  }
  
  // 创建错误故事（当主要流程失败时）
  static createErrorStory(content, genre, style, errorMessage) {
    console.log('创建错误故事...')
    return {
      id: `story-error-${Date.now()}`,
      metadata: {
        title: "故事创作遇到问题",
        genre: genre,
        style: style,
        createdAt: new Date().toISOString(),
        version: '1.0',
        error: errorMessage
      },
      outline: {
        title: "故事创作遇到问题",
        summary: `基于您提供的故事内容"${content.substring(0, 50)}..."，AI创作过程中遇到了问题：${errorMessage}`,
        chapters: [
          {
            id: 1,
            title: "开篇",
            description: "故事的开端",
            scenes: 1
          }
        ],
        characters: [
          {
            name: "主角",
            role: "protagonist",
            description: "故事的主要角色"
          }
        ],
        mainPlot: "故事将围绕您提供的内容展开",
        branches: [],
        genre: genre,
        style: style
      },
      characters: [
        {
          id: "char-error-1",
          name: "主角",
          role: "protagonist",
          description: "故事的主要角色",
          appearance: "年轻有为，眼神坚定",
          background: "背景故事将在后续完善",
          personality: "勇敢、正义、有同情心",
          relationships: "与其他角色的关系将在故事发展中揭示",
          stats: { intelligence: 70, strength: 70, charisma: 70, wisdom: 70 },
          imagePrompt: "young protagonist, determined expression"
        }
      ],
      scenes: [
        {
          id: "scene-error-1",
          title: "开篇 - 场景1",
          description: "故事的开端，介绍主要角色和背景设定",
          content: "这是一个基本的场景内容，由于AI创作过程中遇到问题，内容较为简单。",
          choices: [
            {
              text: "继续前进",
              consequence: "继续故事",
              nextScene: "下一场景"
            }
          ],
          imagePrompt: "story opening scene, character introduction",
          imageUrl: "https://example.com/images/error-scene.jpg",
          characters: [],
          chapterId: 1,
          chapterTitle: "开篇"
        }
      ],
      connections: [],
      structure: {
        chapters: [
          {
            id: 1,
            title: "开篇",
            description: "故事的开端",
            scenes: 1
          }
        ],
        branches: [],
        mainPlot: "故事将围绕您提供的内容展开"
      },
      stats: {
        totalScenes: 1,
        totalCharacters: 1,
        totalChoices: 1,
        totalBranches: 0,
        totalEndings: 1,
        estimatedPlayTime: "2 分钟",
        complexity: "低",
        replayability: "低"
      }
    }
  }
}
